/**
 * 跳数搜索算法 - 独立的图遍历算法，不依赖任何UI组件
 * 功能：根据起始节点和跳数，计算出需要显示的节点和边ID集合
 */

export interface GraphData {
  nodes: Array<{
    id: string;
    type: string;
    group?: string;
    parentId?: string;
    groupId?: string;
    [key: string]: any;
  }>;
  edges: Array<{
    id: string;
    sourceNodeId: string;
    targetNodeId: string;
    [key: string]: any;
  }>;
}

export interface JumpSearchResult {
  visibleNodeIds: string[];
  visibleEdgeIds: string[];
  details: {
    totalNodes: number;
    totalEdges: number;
    bfsNodes: string[];
    groupCompletedNodes: string[];
  };
}

/**
 * 检查节点是否为分组类型
 */
function isGroupNode(nodeId: string, graphData: GraphData): boolean {
  const node = graphData.nodes.find(n => n.id === nodeId);
  return node?.type === 'dynamic-group' || node?.type === 'group';
}

/**
 * 获取分组的所有子节点
 */
function getGroupChildren(groupId: string, graphData: GraphData): string[] {
  const children: string[] = [];
  
  // 方法1：从分组节点的children属性获取
  const groupNode = graphData.nodes.find(n => n.id === groupId);
  if (groupNode?.children && Array.isArray(groupNode.children)) {
    children.push(...groupNode.children);
  }
  if (groupNode?.properties?.children && Array.isArray(groupNode.properties.children)) {
    children.push(...groupNode.properties.children);
  }
  
  // 方法2：遍历所有节点，查找属于该分组的节点
  graphData.nodes.forEach(node => {
    // 检查节点是否属于该分组（支持多种分组属性）
    if (
      node.group === groupId ||
      node.parentId === groupId ||
      node.groupId === groupId ||
      node.properties?.parentGroupId === groupId ||
      node.properties?.group === groupId ||
      node.properties?.parentId === groupId
    ) {
      if (!children.includes(node.id)) {
        children.push(node.id);
      }
    }
  });
  
  return children;
}

/**
 * 获取节点所属的分组ID
 */
function getNodeGroup(nodeId: string, graphData: GraphData): string | null {
  const node = graphData.nodes.find(n => n.id === nodeId);
  
  // 检查多种可能的分组属性
  return (
    node?.group ||
    node?.parentId ||
    node?.groupId ||
    node?.properties?.parentGroupId ||
    node?.properties?.group ||
    node?.properties?.parentId ||
    null
  );
}

/**
 * 获取与指定节点直接相连的所有节点
 */
function getDirectConnectedNodes(nodeId: string, graphData: GraphData): string[] {
  const connected: string[] = [];
  
  graphData.edges.forEach(edge => {
    if (edge.sourceNodeId === nodeId) {
      connected.push(edge.targetNodeId);
    } else if (edge.targetNodeId === nodeId) {
      connected.push(edge.sourceNodeId);
    }
  });

  return connected;
}

/**
 * 获取与指定节点相连的所有节点（包括分组逻辑处理）
 */
function getConnectedNodes(nodeId: string, graphData: GraphData): string[] {
  const connected: string[] = [];
  
  // 获取直接连接
  const directConnections = getDirectConnectedNodes(nodeId, graphData);
  connected.push(...directConnections);
  
  // 如果当前节点是分组，还需要检查子节点的连接
  if (isGroupNode(nodeId, graphData)) {
    const children = getGroupChildren(nodeId, graphData);
    
    children.forEach(childId => {
      const childConnections = getDirectConnectedNodes(childId, graphData);
      childConnections.forEach(connectedId => {
        // 避免将分组内部的连接添加到外部连接中
        if (
          !children.includes(connectedId) &&
          connectedId !== nodeId &&
          !connected.includes(connectedId)
        ) {
          connected.push(connectedId);
        }
      });
    });
  }

  return connected;
}

/**
 * BFS算法获取指定跳数范围内的节点
 */
function bfsSearch(startNodeId: string, maxJumps: number, graphData: GraphData): Set<string> {
  const visited = new Set<string>();
  const queue: Array<{ nodeId: string; jumps: number }> = [
    { nodeId: startNodeId, jumps: 0 },
  ];

  while (queue.length > 0) {
    const { nodeId, jumps } = queue.shift()!;

    if (visited.has(nodeId) || jumps > maxJumps) continue;

    visited.add(nodeId);

    if (jumps < maxJumps) {
      // 找到所有相邻节点
      const connectedNodes = getConnectedNodes(nodeId, graphData);

      connectedNodes.forEach(connectedNodeId => {
        if (!visited.has(connectedNodeId)) {
          queue.push({ nodeId: connectedNodeId, jumps: jumps + 1 });
        }
      });
    }
  }

  return visited;
}

/**
 * 补全分组关系：确保分组的完整性
 */
function completeGroupRelations(nodeIds: Set<string>, graphData: GraphData): Set<string> {
  const completed = new Set(nodeIds);

  // 遍历所有已选中的节点
  nodeIds.forEach(nodeId => {
    // 情况1：如果是分组节点，添加所有子节点
    if (isGroupNode(nodeId, graphData)) {
      const children = getGroupChildren(nodeId, graphData);
      children.forEach(childId => {
        completed.add(childId);
      });
    } 
    // 情况2：如果是子节点，添加其分组和所有兄弟节点
    else {
      const groupId = getNodeGroup(nodeId, graphData);
      if (groupId) {
        // 添加分组节点本身
        completed.add(groupId);
        // 添加所有兄弟节点
        const siblings = getGroupChildren(groupId, graphData);
        siblings.forEach(siblingId => {
          completed.add(siblingId);
        });
      }
    }
  });

  return completed;
}

/**
 * 获取可见的边ID集合
 */
function getVisibleEdges(visibleNodeIds: Set<string>, graphData: GraphData): string[] {
  return graphData.edges
    .filter(edge =>
      visibleNodeIds.has(edge.sourceNodeId) && visibleNodeIds.has(edge.targetNodeId)
    )
    .map(edge => edge.id);
}

/**
 * 主要的跳数搜索算法
 * @param startNodeId 起始节点ID
 * @param maxJumps 最大跳数（1-3）
 * @param graphData 完整的图数据
 * @returns 搜索结果，包含可见的节点和边ID
 */
export function jumpSearch(
  startNodeId: string,
  maxJumps: number,
  graphData: GraphData
): JumpSearchResult {
  // 验证输入参数
  if (!startNodeId) {
    throw new Error('起始节点ID不能为空');
  }
  
  if (maxJumps < 1 || maxJumps > 3) {
    throw new Error('跳数必须在1-3之间');
  }
  
  if (!graphData.nodes.some(n => n.id === startNodeId)) {
    throw new Error(`起始节点 ${startNodeId} 不存在于图数据中`);
  }

  // 第一步：BFS搜索获取基础节点集合
  const bfsNodes = bfsSearch(startNodeId, maxJumps, graphData);
  
  // 第二步：补全分组关系
  const completedNodes = completeGroupRelations(bfsNodes, graphData);
  
  // 第三步：获取可见的边
  const visibleEdgeIds = getVisibleEdges(completedNodes, graphData);

  return {
    visibleNodeIds: Array.from(completedNodes),
    visibleEdgeIds,
    details: {
      totalNodes: completedNodes.size,
      totalEdges: visibleEdgeIds.length,
      bfsNodes: Array.from(bfsNodes),
      groupCompletedNodes: Array.from(completedNodes).filter(id => !bfsNodes.has(id)),
    }
  };
}

/**
 * 测试用的示例函数 - 使用真实数据结构
 */
export function testJumpSearch() {
  const testGraphData: GraphData = {
    nodes: [
      { id: 'node_1', type: 'rect', properties: { name: '实体1' } },
      { id: 'node_3', type: 'rect', properties: { name: '实体3' } },
      { 
        id: 'node_2', 
        type: 'dynamic-group', 
        properties: { name: '实体2', isEntityGroup: true },
        children: ['node_2_state_1', 'node_2_state_2']
      },
      { 
        id: 'node_2_state_1', 
        type: 'state', 
        properties: { name: '状态1', parentGroupId: 'node_2', isState: true }
      },
      { 
        id: 'node_2_state_2', 
        type: 'state', 
        properties: { name: '状态2', parentGroupId: 'node_2', isState: true }
      },
    ],
    edges: [
      { id: 'edge1', sourceNodeId: 'node_1', targetNodeId: 'node_2' },
      { id: 'edge2', sourceNodeId: 'node_2', targetNodeId: 'node_3' },
    ]
  };

  console.log('🧪 测试跳数搜索算法 (基于真实数据结构):');
  
  // 测试：从实体3开始1跳，应该显示实体3和实体2（包括实体2的所有状态）
  console.log('\n--- 测试：从实体3开始1跳 ---');
  const result1 = jumpSearch('node_3', 1, testGraphData);
  console.log('1跳结果:', result1);
  
  // 测试：从实体3开始2跳，应该还能到达实体1
  console.log('\n--- 测试：从实体3开始2跳 ---');
  const result2 = jumpSearch('node_3', 2, testGraphData);
  console.log('2跳结果:', result2);
  
  // 测试：从状态1开始1跳，应该显示整个实体2分组
  console.log('\n--- 测试：从状态1开始1跳 ---');
  const result3 = jumpSearch('node_2_state_1', 1, testGraphData);
  console.log('状态1的1跳结果:', result3);
}